import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { usePersonalizedFeed } from "@/hooks/usePersonalizedFeed";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const FALLBACK_ARTISTS = [
  { id: "K8vZ9171oZ7", name: "Taylor Swift", genre: "Pop", fans: "14.2K", img: "https://s1.ticketm.net/dam/a/200/e0e5a333-c375-467a-aeb5-0e4a3e3c6200_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 42 },
  { id: "K8vZ917GSz7", name: "Bad Bunny", genre: "Reggaeton", fans: "11.8K", img: "https://s1.ticketm.net/dam/a/4b6/907e5a60-e7d0-4e35-a1b5-84aa1f9b74b6_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 28 },
  { id: "K8vZ9171K50", name: "Beyoncé", genre: "R&B", fans: "18.5K", img: "https://s1.ticketm.net/dam/a/c4c/e457df70-e4c1-42aa-93c2-48867ee41c4c_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 35 },
  { id: "K8vZ9174v57", name: "Drake", genre: "Hip-Hop", fans: "9.7K", img: "https://s1.ticketm.net/dam/a/8b3/fd2a1b09-e80a-43f0-8f53-f5e0e1a0d8b3_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 18 },
  { id: "K8vZ9174K37", name: "SZA", genre: "R&B", fans: "8.3K", img: "https://s1.ticketm.net/dam/a/41d/21db5ad1-3cce-4b02-8bba-fc7c24eb141d_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 22 },
  { id: "K8vZ9171C-f", name: "The Weeknd", genre: "R&B/Pop", fans: "12.1K", img: "https://s1.ticketm.net/dam/a/6e5/ca4ef498-e290-4659-972b-2d0978e1c6e5_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 30 },
  { id: "K8vZ9171K9f", name: "Billie Eilish", genre: "Pop", fans: "10.5K", img: "https://s1.ticketm.net/dam/a/781/1a84e98c-1da8-4365-b933-4d0b82668781_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 25 },
  { id: "K8vZ9174onV", name: "Post Malone", genre: "Hip-Hop/Pop", fans: "7.9K", img: "https://s1.ticketm.net/dam/a/040/4c305e3e-e35c-42f3-a04d-1ea5dab10040_RETINA_PORTRAIT_16_9.jpg", upcomingEvents: 20 },
];

const formatFans = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

const RecommendedArtists = () => {
  const { artists, personalized, loading } = usePersonalizedFeed();
  const navigate = useNavigate();
  const displayArtists = artists.length > 0 ? artists : FALLBACK_ARTISTS;
  const [fanCounts, setFanCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    const loadFanCounts = async () => {
      const artistIds = displayArtists.map((artist) => artist.id).filter(Boolean);
      if (artistIds.length === 0) {
        setFanCounts({});
        return;
      }

      const { data } = await (supabase as any)
        .from("artist_fans")
        .select("artist_id")
        .in("artist_id", artistIds);

      const counts: Record<string, number> = {};
      for (const artistId of artistIds) counts[artistId] = 0;
      for (const row of data ?? []) {
        counts[row.artist_id] = (counts[row.artist_id] ?? 0) + 1;
      }
      setFanCounts(counts);
    };

    loadFanCounts();
  }, [displayArtists]);

  return (
    <section className="py-14 sm:py-20 border-t border-border/50 relative overflow-visible">
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[400px] h-[400px] bg-concert-warm/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex items-end justify-between"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-1 w-8 rounded-full bg-concert-warm" />
              <span className="text-xs font-display font-bold text-concert-warm uppercase tracking-widest">Artists</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
              {personalized ? "Recommended For You" : "Popular Artists"}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {personalized ? "Based on your activity & concert history" : "Popular artists on Encore Republic"}
            </p>
          </div>
          <button
            onClick={() => navigate("/artists")}
            className="flex items-center gap-1 text-sm font-display font-medium text-primary hover:underline underline-offset-4"
          >
            View all <ChevronRight className="h-4 w-4" />
          </button>
        </motion.div>

        <div className="flex gap-4 sm:gap-5 overflow-x-auto pb-6 scrollbar-hide -mx-4 px-4" style={{ paddingTop: "2rem", marginTop: "-2rem" }}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-32 sm:w-40">
                  <Skeleton className="mx-auto mb-3 h-24 w-24 sm:h-28 sm:w-28 rounded-full" />
                  <Skeleton className="mx-auto h-4 w-20 mb-1" />
                  <Skeleton className="mx-auto h-3 w-14" />
                </div>
              ))
            : displayArtists.map((artist, i) => (
                <motion.div
                  key={artist.id}
                  initial={{ opacity: 0, y: 30, scale: 0.8 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -10, transition: { duration: 0.25 } }}
                  className="flex-shrink-0 w-32 sm:w-40 cursor-pointer group"
                  onClick={() => navigate(`/artist/${artist.id}`)}
                >
                  <motion.div
                    className="relative mx-auto mb-4 h-24 w-24 sm:h-28 sm:w-28 rounded-full transition-all duration-300"
                    whileHover={{
                      boxShadow: "0 0 40px hsl(25 95% 53% / 0.4), 0 0 80px hsl(25 95% 53% / 0.15)",
                      scale: 1.08,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="absolute inset-0 rounded-full p-[3px] gradient-premium opacity-50 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="h-full w-full rounded-full overflow-hidden bg-card">
                        <img src={artist.img} alt={artist.name} loading="lazy" width={112} height={112} className="h-full w-full object-cover" />
                      </div>
                    </div>
                  </motion.div>
                  <div className="text-center">
                    <p className="font-display font-bold text-sm text-foreground truncate">{artist.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{artist.genre || "Music"}</p>
                    <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5">
                      <span className="text-[10px] font-display font-semibold text-primary">
                        {fanCounts[artist.id] > 0
                          ? `${formatFans(fanCounts[artist.id])} fans`
                          : artist.upcomingEvents > 0
                            ? `${artist.upcomingEvents} upcoming`
                            : artist.fans || "New"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
        </div>
      </div>
    </section>
  );
};

export default RecommendedArtists;
